import { useEffect, useRef } from 'react';

import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';

import {
  useCommentApi,
  useCommentState,
} from '@spotify/plugin-confidence-comments-react';

const withBlockId = (base: string, blockId: string | null): string =>
  blockId ? `${base}/${blockId}` : base;

const getZoneIdFromNode = (node: ProseMirrorNode): string | null => {
  if (!node.attrs) return null;

  switch (node.type.name) {
    case 'calloutBlock':
      return withBlockId('callout-block', node.attrs.blockId ?? null);
    case 'imageCard':
      return withBlockId('image-card', node.attrs.blockId ?? null);
    case 'image':
      return withBlockId('image', node.attrs.blockId ?? null);
    case 'codeBlock':
      return withBlockId('code-block', node.attrs.blockId ?? null);
    case 'blockquote':
      return withBlockId('blockquote', node.attrs.blockId ?? null);
    case 'highlightBlock':
      return withBlockId('highlight', node.attrs.blockId ?? null);
    case 'variantCard':
      return node.attrs.treatmentName
        ? `variant-card/${node.attrs.treatmentName}`
        : 'variant-card';
    case 'metricCard':
      return node.attrs.metricId
        ? `metric-card/${node.attrs.metricId}`
        : 'metric-card';
    case 'metricTable':
      return withBlockId('metric-table', node.attrs.blockId ?? null);
    case 'table':
      return withBlockId('table', node.attrs.blockId ?? null);
    case 'tag':
      return withBlockId('tag', node.attrs.blockId ?? null);
    default:
      return null;
  }
};

const getZoneIdsFromDoc = (doc: ProseMirrorNode): Set<string> => {
  const zones = new Set<string>();
  doc.descendants(node => {
    const zoneId = getZoneIdFromNode(node);
    if (zoneId) {
      zones.add(zoneId);
    }
  });
  return zones;
};

const getInlineCommentIdsFromDoc = (doc: ProseMirrorNode): Set<string> => {
  const commentIds = new Set<string>();
  doc.descendants(node => {
    if (!node.isText) return;

    node.marks.forEach(mark => {
      if (mark.type.name === 'comment' && mark.attrs.commentId) {
        const commentId = mark.attrs.commentId as string;
        // Only track inline comments (those starting with 'tiptap-comment-')
        if (commentId.startsWith('tiptap-comment-')) {
          commentIds.add(commentId);
        }
      }
    });
  });
  return commentIds;
};

export const useBlockCommentCleanup = (
  editor: Editor | null,
  resource: string,
) => {
  const comments = useCommentState(state => state.comments);
  const { resolveComment } = useCommentApi(resource);

  const commentsRef = useRef(comments);
  const resolveCommentRef = useRef(resolveComment);
  const previousZonesRef = useRef<Set<string>>(new Set());
  const previousInlineCommentIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  useEffect(() => {
    resolveCommentRef.current = resolveComment;
  }, [resolveComment]);

  useEffect(() => {
    if (!editor) return;
    previousZonesRef.current = getZoneIdsFromDoc(editor.state.doc);
    previousInlineCommentIdsRef.current = getInlineCommentIdsFromDoc(
      editor.state.doc,
    );
  }, [editor]);

  useEffect(() => {
    if (!editor) return undefined;

    const handleTransaction = ({
      transaction,
    }: {
      transaction: { docChanged: boolean };
    }) => {
      if (!transaction.docChanged) return;

      const currentZones = getZoneIdsFromDoc(editor.state.doc);
      const previousZones = previousZonesRef.current;

      // Check for removed block zones
      for (const zone of previousZones) {
        if (!currentZones.has(zone)) {
          const openComments = commentsRef.current.filter(
            c => !c.resolved && c.anchor?.zone === zone,
          );

          for (const comment of openComments) {
            resolveCommentRef.current(comment.name).catch(() => {
              // Silently handle all errors to prevent unhandled promise rejections
            });
          }
        }
      }

      previousZonesRef.current = currentZones;

      const currentInlineCommentIds = getInlineCommentIdsFromDoc(
        editor.state.doc,
      );
      const previousInlineCommentIds = previousInlineCommentIdsRef.current;

      // Check for removed inline comment marks
      for (const commentId of previousInlineCommentIds) {
        if (!currentInlineCommentIds.has(commentId)) {
          const openComments = commentsRef.current.filter(
            c => !c.resolved && c.anchor?.zone === commentId,
          );

          for (const comment of openComments) {
            resolveCommentRef.current(comment.name).catch(() => {
              // Silently handle all errors to prevent unhandled promise rejections
            });
          }
        }
      }

      previousInlineCommentIdsRef.current = currentInlineCommentIds;
    };

    editor.on('transaction', handleTransaction);

    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor]);
};
